import React, { useEffect } from "react";
import { Card, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import {
  BsFillPlayCircleFill,
  BsFileEarmarkTextFill,
  BsImageFill,
  BsArrowUpRightSquare,
  BsLayersHalf,
} from "react-icons/bs";

const Media = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Operational Media Vault & Field Assets | NGO Waters";
  }, []);

  const mediaContent = [
    {
      type: "image",
      title: "Commercial Bottling & Automated Packaging Line",
      description:
        "A comprehensive look into our multi-stage clean bottling, capping, and automated sealing matrices.",
      src: "https://images.unsplash.com/photo-1615485290382-441e4d049cb5?auto=format&fit=crop&q=80&w=800",
      tag: "Plant Infrastructure",
    },
    {
      type: "video",
      title: "7-Stage Reverse Osmosis & UV Sterilization Process",
      description:
        "Take a virtual tour inside our advanced laboratory facility showcasing real-time trace mineral monitoring.",
      src: "https://www.youtube.com/embed/dQw4w9WgXcQ", // Replace with actual company asset video ID when available
      tag: "Technical Audit",
    },
    {
      type: "image",
      title: "Deep Boring Projects & Rural Supply Points",
      description:
        "Documenting our infrastructure team building deep tube-wells and high-capacity pipelines across local sectors.",
      src: "https://images.unsplash.com/photo-1541944743827-e04aa6427c33?auto=format&fit=crop&q=80&w=800",
      tag: "Ground Operations",
    },
    {
      type: "article",
      title: "IS 14543 Compliance & Microbiological Integrity",
      description:
        "An expert analytical overview of the chemical parameters, trace-mineral ratios, and safety protocols governing our operational grid.",
      src: "#/documentation/compliance-report", // Upstream documentation route
      tag: "Research Paper",
    },
  ];

  const cardSpring = { type: "spring", stiffness: 260, damping: 20 };

  return (
    <motion.div
      className="container-fluid min-vh-100 py-5 px-3 px-md-5 bg-light"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Visual Accent Orbs */}
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "450px",
          height: "450px",
          background: "rgba(54, 191, 216, 0.02)",
          top: "10%",
          left: "5%",
          filter: "blur(120px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "550px",
          height: "550px",
          background: "rgba(26, 60, 107, 0.03)",
          bottom: "5%",
          right: "2%",
          filter: "blur(140px)",
          pointerEvents: "none",
        }}
      />

      {/* ================= HEADER BLOCK ================= */}
      <header className="container py-4 mb-5 text-center position-relative z-1">
        <span
          className="badge px-3 py-2 mb-3 text-uppercase fw-semibold"
          style={{
            backgroundColor: "rgba(26, 60, 107, 0.06)",
            color: "#1a3c6b",
            fontSize: "0.7rem",
            borderRadius: "6px",
            letterSpacing: "0.5px",
          }}
        >
          Digital Asset Repository
        </span>
        <h1
          className="display-5 fw-bold text-dark mb-3"
          style={{ letterSpacing: "-1.2px" }}
        >
          Media & Operation Logs
        </h1>
        <p
          className="text-muted small mx-auto mb-0"
          style={{ maxWidth: "550px", lineHeight: "1.6" }}
        >
          A secure compilation of structural images, engineering videos, and
          technical documentation illustrating the workflow pipelines of Ponia
          Food and Beverage Private Limited.
        </p>
      </header>

      {/* ================= MEDIA MESH GRID ================= */}
      <main className="container position-relative z-1 mb-5">
        <Row className="g-4">
          {mediaContent.map((item, index) => (
            <Col lg={6} xl={6} key={index}>
              <motion.div
                whileHover={{
                  y: -5,
                  boxShadow: "0px 15px 30px rgba(0,0,0,0.04)",
                }}
                transition={cardSpring}
                className="h-100"
              >
                <Card
                  className="border-0 h-100 bg-white overflow-hidden shadow-sm"
                  style={{ borderRadius: "20px" }}
                >
                  {/* Conditionally Render Media Viewports based on Node Specification */}
                  {item.type === "image" && (
                    <div
                      className="position-relative overflow-hidden"
                      style={{ height: "260px" }}
                    >
                      <img
                        src={item.src}
                        alt={item.title}
                        className="w-100 h-100 img-fluid"
                        style={{ objectFit: "cover" }}
                      />
                      <span
                        className="position-absolute top-3 start-3 badge bg-dark text-white px-2.5 py-1.5 d-flex align-items-center gap-1.5 style-tag"
                        style={{
                          borderRadius: "6px",
                          fontSize: "0.68rem",
                          backgroundColor: "rgba(0,0,0,0.7) !important",
                        }}
                      >
                        <BsImageFill /> {item.tag}
                      </span>
                    </div>
                  )}

                  {item.type === "video" && (
                    <div
                      className="position-relative bg-dark overflow-hidden"
                      style={{ height: "260px" }}
                    >
                      <iframe
                        className="w-100 h-100 border-0"
                        src={item.src}
                        title={item.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                      <span
                        className="position-absolute top-3 start-3 badge bg-danger text-white px-2.5 py-1.5 d-flex align-items-center gap-1.5"
                        style={{ borderRadius: "6px", fontSize: "0.68rem" }}
                      >
                        <BsFillPlayCircleFill /> {item.tag}
                      </span>
                    </div>
                  )}

                  {item.type === "article" && (
                    <div
                      className="d-flex align-items-center justify-content-center bg-light position-relative overflow-hidden"
                      style={{
                        height: "260px",
                        borderBottom: "1px solid rgba(0,0,0,0.03)",
                      }}
                    >
                      <div
                        className="position-absolute rounded-circle"
                        style={{
                          width: "140px",
                          height: "140px",
                          backgroundColor: "rgba(26, 60, 107, 0.04)",
                        }}
                      />
                      <BsFileEarmarkTextFill className="position-relative z-1 display-3 text-secondary opacity-50" />
                      <span
                        className="position-absolute top-3 start-3 badge bg-dark text-white px-2.5 py-1.5 d-flex align-items-center gap-1.5"
                        style={{ borderRadius: "6px", fontSize: "0.68rem" }}
                      >
                        <BsFileEarmarkTextFill /> {item.tag}
                      </span>
                    </div>
                  )}

                  {/* ================= CARD TEXT CONTENT ================= */}
                  <Card.Body className="p-4 d-flex flex-column justify-content-between">
                    <div>
                      <Card.Title
                        className="fw-bold mb-2 text-dark h5"
                        style={{ letterSpacing: "-0.3px", lineHeight: "1.4" }}
                      >
                        {item.title}
                      </Card.Title>
                      <Card.Text
                        className="text-secondary small mb-4"
                        style={{ lineHeight: "1.6" }}
                      >
                        {item.description}
                      </Card.Text>
                    </div>

                    {/* Action Layer Synchronization */}
                    <div className="pt-2 border-top border-light d-flex align-items-center justify-content-between">
                      <span
                        className="text-uppercase fw-bold text-muted"
                        style={{ fontSize: "0.65rem", letterSpacing: "1px" }}
                      >
                        Format: {item.type}
                      </span>
                      {item.type === "article" && (
                        <a
                          href={item.src}
                          className="btn btn-outline-dark btn-sm px-3 py-1.5 fw-semibold d-flex align-items-center gap-1"
                          style={{ borderRadius: "8px", fontSize: "0.78rem" }}
                        >
                          Access Report <BsArrowUpRightSquare />
                        </a>
                      )}
                      {item.type === "video" && (
                        <span
                          className="text-primary small fw-semibold d-flex align-items-center gap-1"
                          style={{ fontSize: "0.78rem" }}
                        >
                          Stream Enabled
                        </span>
                      )}
                      {item.type === "image" && (
                        <span
                          className="text-success small fw-semibold d-flex align-items-center gap-1"
                          style={{ fontSize: "0.78rem" }}
                        >
                          Verified Capture
                        </span>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>
      </main>

      {/* ================= BASE FOOTER STATEMENT ================= */}
      <footer className="container mt-5">
        <div
          className="p-4 text-center border-top text-muted"
          style={{ borderColor: "rgba(0,0,0,0.06)", fontSize: "0.75rem" }}
        >
          <div className="d-flex align-items-center justify-content-center gap-1.5 mb-1 text-secondary fw-semibold">
            <BsLayersHalf />
            <span>
              Ponia Food & Beverage Pvt. Ltd. Corporate Registry Database
            </span>
          </div>
          All multimedia configurations, streaming records, and document
          blueprints are indexed assets under regulatory safety standards.
        </div>
      </footer>
    </motion.div>
  );
};

export default Media;
